// ── Reading a row of Account Activity ────────────────────────────────────────
//
// The panel and the CSV both had to decide "is this row money, or credits?",
// and both answered it the same wrong way:
//
//     tx.type === 'invoice' ? formatMoney(...) : creditDelta
//
// The server has never sent `invoice`. Real types are credit_purchase,
// subscription_create, plan_upgrade, plan_downgrade, proration_charge,
// proration_credit, renewal, refund, credit_expiry, plan_grant. So the money
// branch was dead code: on screen every row fell through to the credit delta —
// null on a paid row, rendering "+null" — and in the CSV the Amount and Credits
// columns came out empty on every line, which is most of the point of an export.
//
// The lesson is in the shape of the bug rather than the typo: a row was being
// classified by an enum the caller had to keep in step with the server. It does
// not need to be. A row either carries an amount or it does not, and either
// carries a credit movement or it does not — both are answerable from the row
// itself, and a new transaction type cannot break it.
//
// Shared so the two surfaces cannot drift again, which is what let this survive.

import { formatMoney } from './money';

/** Does this row involve money at all? Zero is money — a $0.00 change is a fact. */
export const hasAmount = (tx) => tx != null && tx.amount_cents != null && tx.amount_cents !== '';

/** Does this row move credits? */
export const hasCredits = (tx) => {
  if (tx == null || tx.credit_delta == null || tx.credit_delta === '') return false;
  return Number.isFinite(Number(tx.credit_delta));
};

/**
 * The money column, formatted — or '' when the row is not a money event.
 *
 * Empty rather than "$0.00" for a credit-only row: a grant that cost nothing is
 * not a zero-pound purchase, and printing one invites the reader to add it up.
 */
export function activityAmount(tx) {
  if (!hasAmount(tx)) return '';
  return formatMoney(Number(tx.amount_cents), tx.currency || 'usd');
}

/**
 * The credits column — a signed delta, or '' when no credits moved.
 *
 * Signed because the sign is the meaning: +100 on a top-up, -100 when a trial
 * expires. An unsigned number would make a loss look like a gain.
 */
export function activityCredits(tx) {
  if (!hasCredits(tx)) return '';
  const delta = Number(tx.credit_delta);
  return `${delta > 0 ? '+' : ''}${delta.toLocaleString()}`;
}

/**
 * What the row's headline figure should be in the list.
 *
 * A top-up is both a payment AND a credit grant, so it has two numbers. The
 * list has room for one, and the money is the one a person scanning their
 * account activity is looking for; the credits appear beside it as a subtitle.
 * The CSV has room for both and prints both.
 */
export function activityPrimaryValue(tx) {
  if (hasAmount(tx)) return activityAmount(tx);
  if (hasCredits(tx)) return `${activityCredits(tx)} credits`;
  return '—';
}

/** True when the row should read as a reduction (expiry, refund, downgrade credit). */
export const isDecrease = (tx) =>
  (hasCredits(tx) && Number(tx.credit_delta) < 0)
  || (hasAmount(tx) && Number(tx.amount_cents) < 0);
